// AUTO-GENERATED from servers/common-servers-v2/helpers/FormHelper/validate_submission.js by servers/common-servers-v2/scripts/sync-form-helper.js.
// Do not edit: change the source and run `node scripts/sync-form-helper.js`.
/* eslint-disable */
import {
    OPTION_FIELD_TYPES,
    MULTI_OPTION_FIELD_TYPES,
    CUSTOM_FIELD_PREFIX,
    LIMITS,
    FILE_TYPES,
    FILE_FORMATS,
    FILE_FORMAT_ALIASES,
    DEFAULT_DATE_MODE,
    DATE_MODES,
    VALIDATION_FIELD_TYPES,
} from "./constants.js";
import { resolve_form_state } from "./resolve_form_state.js";
import { is_empty_value, evaluate_condition_item, condition_field_refs, comparable } from "./conditions.js";
import { normalize_digits } from "./normalize_fa.js";
import { compile_regex } from "./regex.js";
import { parse_stored_date } from "./date.js";

/*
 * validate_submission(version, values, options) -> { clean_values, errors }
 *
 * The only thing the server trusts about a submission is the version it points
 * at: values are checked against that version's fields, never against what the
 * client claims (spec §3, §5, §10, §11).
 *
 *   clean_values  fields of non-repeatable groups flat under their key, repeatable
 *                 groups as arrays of objects; hidden / disabled-without-default /
 *                 empty values left out; options rewritten as { title, value } with
 *                 the title taken from the version; strings trimmed and stripped of
 *                 control characters; normalized mobile / digits.
 *   errors        [{ key, group_key, index, path, code, ...details }]; `index` is the
 *                 repeat instance (null outside repeatable groups); `path` is `key` or
 *                 `group_key.index.key`. Codes are stable, the clients map them to texts.
 *
 * options:
 *   mode           "input" (default): file values arrive as { content, file_name, is_base_64 };
 *                  "stored": file values are { media_id, url, name, size } (re-checking saved data).
 *   limits         overrides for LIMITS (tests, per-site tightening).
 *   geo            { states: [{ name, value }], cities: [{ name, value, state_id }] } to check
 *                  state / city membership; without it only the shape is checked.
 *   custom_fields  { [custom_key]: { value_type } } from the site template.
 *   regex_test     (regex, text) -> true | false | null: runs a field's pattern for the caller (the server gives one with a
 *                  time limit); without it the pattern is tested directly. null counts as a mismatch.
 *
 * A value above its length is rejected, never truncated.
 */

const is_plain_object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const has_own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const as_list = (value) => (Array.isArray(value) ? value : []);

/* eslint-disable no-control-regex */
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u202A-\u202E\u2066-\u2069]/g;
const CONTROL_CHARS_KEEP_BREAKS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u202A-\u202E\u2066-\u2069]/g;
/* eslint-enable no-control-regex */

// Trim and strip control characters. Single line turns tabs / line breaks into spaces.
const clean_string = (value, { multiline = false } = {}) => {
    if (multiline) {
        return value.replace(/\r\n?/g, "\n").replace(CONTROL_CHARS_KEEP_BREAKS, "").trim();
    }
    return value.replace(/[\t\n\r]/g, " ").replace(CONTROL_CHARS, "").trim();
};

const text_size = (text) => (typeof TextEncoder === "function" ? new TextEncoder().encode(text).length : text.length);

// JSON size of the values, ignoring the base64 body of files (it has its own 5 MB limit).
const values_json_size = (values) => {
    try {
        const json = JSON.stringify(values, function replacer(key, value) {
            if (key === "content" && typeof value === "string" && this && this.is_base_64 === true) {
                return "";
            }
            return value;
        });
        return text_size(json ?? "");
    } catch {
        return Infinity;
    }
};

const ok = (value) => ({ value });
const fail = (code, details = {}) => ({ error: { code, ...details } });

// ---- single value validators -------------------------------------------------

const validate_text = (raw, field, limits, multiline, regex_test) => {
    if (typeof raw !== "string") {
        return fail("invalid_type");
    }
    const value = clean_string(raw, { multiline });
    const max = multiline ? limits.TEXTAREA_MAX : limits.TEXT_MAX;
    if (value.length > max) {
        return fail("too_long", { max });
    }
    const source = field.data?.regex;
    if (value !== "" && typeof source === "string" && source !== "") {
        const regex = compile_regex(source);
        // `regex_test` is the server's way to run an admin's pattern against a visitor's text with a time limit (the pattern
        // was judged safe when the form was saved, this is the second line); null from it means it ran out of time
        if (regex && !(regex_test ? regex_test(regex, value) === true : regex.test(value))) {
            return fail("pattern_mismatch");
        }
    }
    return ok(value);
};

const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;

const parse_number = (raw) => {
    if (typeof raw === "number") {
        return Number.isFinite(raw) ? raw : NaN;
    }
    if (typeof raw === "string") {
        const text = normalize_digits(clean_string(raw)).replace(/[,\u066C\u060C\s]/g, "").replace(/\u066B/g, ".");
        return NUMBER_PATTERN.test(text) ? Number(text) : NaN;
    }
    return NaN;
};

const validate_number = (raw, field) => {
    const number = parse_number(raw);
    if (Number.isNaN(number) || Math.abs(number) > Number.MAX_SAFE_INTEGER) {
        return fail("invalid_number");
    }
    const data = field.data || {};
    if (data.float !== true && !Number.isInteger(number)) {
        return fail("not_integer");
    }
    if (Number.isFinite(data.min) && number < data.min) {
        return fail("below_min", { min: data.min });
    }
    if (Number.isFinite(data.max) && number > data.max) {
        return fail("above_max", { max: data.max });
    }
    return ok(number);
};

const validate_price = (raw) => {
    const number = parse_number(raw);
    if (Number.isNaN(number) || !Number.isInteger(number) || number < 0 || number > Number.MAX_SAFE_INTEGER) {
        return fail("invalid_price");
    }
    return ok(number);
};

const option_value_of = (raw) => {
    if (typeof raw === "string") {
        return raw;
    }
    if (is_plain_object(raw) && typeof raw.value === "string") {
        return raw.value;
    }
    return undefined;
};

// The title always comes from the version, never from the client.
const pick_option = (raw, field) => {
    const wanted = option_value_of(raw);
    const found = wanted === undefined ? undefined : as_list(field.options).find((option) => option && option.value === wanted);
    return found ? { title: found.title, value: found.value } : undefined;
};

const validate_single_option = (raw, field) => {
    const option = pick_option(raw, field);
    return option ? ok(option) : fail("invalid_option");
};

const validate_multi_option = (raw, field) => {
    if (!Array.isArray(raw)) {
        return fail("invalid_type");
    }
    const max = as_list(field.options).length;
    if (raw.length > max) {
        return fail("too_many_selected", { max });
    }
    const picked = [];
    const used = new Set();
    for (let i = 0; i < raw.length; i += 1) {
        const option = pick_option(raw[i], field);
        if (!option) {
            return fail("invalid_option");
        }
        if (!used.has(option.value)) {
            used.add(option.value);
            picked.push(option);
        }
    }
    return ok(picked);
};

const validate_date = (raw, field) => {
    if (typeof raw !== "string") {
        return fail("invalid_type");
    }
    const mode = DATE_MODES.includes(field.data?.mode) ? field.data.mode : DEFAULT_DATE_MODE;
    const value = raw.trim();
    return parse_stored_date(value, mode) ? ok(value) : fail("invalid_date", { mode });
};

const BASE64_PATTERN = /^[A-Za-z0-9+/]*={0,2}$/;

const file_extension = (name) => {
    const dot = name.lastIndexOf(".");
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
};

// the extensions a file field takes: the formats the admin chose (`data.accept`, jpg meaning jpeg too), or every FILE_TYPES
const allowed_file_types = (field) => {
    const accept = Array.isArray(field?.data?.accept) ? field.data.accept.filter((item) => FILE_FORMATS.includes(item)) : [];
    if (accept.length === 0) {
        return Object.keys(FILE_TYPES);
    }
    return [...new Set(accept.flatMap((item) => FILE_FORMAT_ALIASES[item] || [item]))].filter((item) => has_own(FILE_TYPES, item));
};

const clean_file_name = (name) => (typeof name === "string" ? clean_string(name).replace(/[\\/]/g, "_") : "");

const validate_file = (raw, field, limits, mode) => {
    if (!is_plain_object(raw)) {
        return fail("invalid_type");
    }
    if (mode === "stored") {
        const name = clean_file_name(raw.name);
        if (typeof raw.media_id !== "string" || raw.media_id === "" || typeof raw.url !== "string" || raw.url === ""
            || name === "" || !Number.isFinite(raw.size) || raw.size < 0) {
            return fail("file_invalid");
        }
        if (!has_own(FILE_TYPES, file_extension(name))) {
            return fail("file_type_not_allowed", { allowed: Object.keys(FILE_TYPES) });
        }
        if (raw.size > limits.FILE_MAX_BYTES) {
            return fail("file_too_large", { max: limits.FILE_MAX_BYTES });
        }
        return ok({ media_id: raw.media_id, url: raw.url, name, size: raw.size });
    }

    // input: { content: <dataURL or base64>, file_name, is_base_64: true }
    if (raw.is_base_64 !== true || typeof raw.content !== "string" || raw.content === "") {
        return fail("file_invalid");
    }
    const file_name = clean_file_name(raw.file_name);
    if (file_name === "" || file_name.length > limits.FILE_NAME_MAX) {
        return fail("file_invalid");
    }
    // a new file: only the formats this field takes (a file stored before keeps what it was accepted with)
    const extension = file_extension(file_name);
    const allowed = allowed_file_types(field);
    if (!allowed.includes(extension)) {
        return fail("file_type_not_allowed", { allowed });
    }
    let payload = raw.content;
    if (payload.startsWith("data:")) {
        const comma = payload.indexOf(",");
        const header = comma > 0 ? payload.slice(5, comma) : "";
        const header_match = /^([\w.+-]+\/[\w.+-]+);base64$/i.exec(header);
        if (!header_match) {
            return fail("file_invalid");
        }
        if (!FILE_TYPES[extension].includes(header_match[1].toLowerCase())) {
            return fail("file_type_not_allowed", { allowed });
        }
        payload = payload.slice(comma + 1);
    }
    if (payload.length % 4 !== 0 || !BASE64_PATTERN.test(payload)) {
        return fail("file_invalid");
    }
    const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
    const size = (payload.length / 4) * 3 - padding;
    if (size <= 0) {
        return fail("file_invalid");
    }
    if (size > limits.FILE_MAX_BYTES) {
        return fail("file_too_large", { max: limits.FILE_MAX_BYTES });
    }
    return ok({ content: raw.content, file_name, is_base_64: true, size });
};

// 09123456789, +989123456789, 00989123456789, 989123456789, 9123456789 -> "09123456789"
const MOBILE_PATTERN = /^(?:\+98|0098|98|0)?(9\d{9})$/;

const validate_mobile = (raw) => {
    if (typeof raw !== "string") {
        return fail("invalid_type");
    }
    const text = normalize_digits(clean_string(raw)).replace(/[\s\-()]/g, "");
    const match = MOBILE_PATTERN.exec(text);
    return match ? ok(`0${match[1]}`) : fail("invalid_mobile");
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const validate_email = (raw) => {
    if (typeof raw !== "string") {
        return fail("invalid_type");
    }
    const value = clean_string(raw).toLowerCase();
    const at = value.indexOf("@");
    if (value.length > 254 || !EMAIL_PATTERN.test(value) || at > 64 || value.includes("..") || value.startsWith(".") || value.slice(0, at).endsWith(".")) {
        return fail("invalid_email");
    }
    return ok(value);
};

const clean_digits = (raw) => normalize_digits(clean_string(raw)).replace(/[\s-]/g, "");

const validate_national_code = (raw) => {
    if (typeof raw !== "string") {
        return fail("invalid_type");
    }
    const digits = clean_digits(raw);
    if (!/^\d{10}$/.test(digits) || /^(\d)\1{9}$/.test(digits)) {
        return fail("invalid_national_code");
    }
    let sum = 0;
    for (let i = 0; i < 9; i += 1) {
        sum += Number(digits[i]) * (10 - i);
    }
    const remainder = sum % 11;
    const check = remainder < 2 ? remainder : 11 - remainder;
    return check === Number(digits[9]) ? ok(digits) : fail("invalid_national_code");
};

const validate_postal_code = (raw) => {
    if (typeof raw !== "string") {
        return fail("invalid_type");
    }
    const digits = clean_digits(raw);
    return /^\d{10}$/.test(digits) && !/^(\d)\1{9}$/.test(digits) ? ok(digits) : fail("invalid_postal_code");
};

const geo_entry_value = (entry) => String(entry?.value ?? entry?.id ?? "");

const validate_geo = (raw, field, context, kind) => {
    const wanted = option_value_of(raw);
    if (wanted === undefined || !/^\d{1,12}$/.test(normalize_digits(wanted))) {
        return fail(`invalid_${kind}`);
    }
    const value = normalize_digits(wanted);
    const list = context.geo ? as_list(kind === "state" ? context.geo.states : context.geo.cities) : null;
    if (!list) {
        // no data to check against: keep the shape, bound the title
        const title = is_plain_object(raw) && typeof raw.title === "string" ? clean_string(raw.title).slice(0, LIMITS.OPTION_TITLE_MAX) : "";
        return ok({ title, value });
    }
    const entry = list.find((item) => geo_entry_value(item) === value);
    if (!entry) {
        return fail(`invalid_${kind}`);
    }
    if (kind === "city" && context.current_state_value !== undefined && entry.state_id !== undefined
        && String(entry.state_id) !== String(context.current_state_value)) {
        return fail("city_state_mismatch");
    }
    return ok({ title: entry.name, value });
};

// Plain JSON only, bounded in depth, size and key names.
const clean_json = (value, limits, depth) => {
    if (depth > limits.CUSTOM_DEPTH_MAX) {
        return { invalid: true };
    }
    if (value === null || typeof value === "boolean") {
        return { value };
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? { value } : { invalid: true };
    }
    if (typeof value === "string") {
        const cleaned = clean_string(value, { multiline: true });
        return cleaned.length > limits.TEXTAREA_MAX ? { invalid: true } : { value: cleaned };
    }
    if (Array.isArray(value)) {
        if (value.length > limits.CUSTOM_ARRAY_MAX) {
            return { invalid: true };
        }
        const list = [];
        for (let i = 0; i < value.length; i += 1) {
            const item = clean_json(value[i], limits, depth + 1);
            if (item.invalid) {
                return item;
            }
            list.push(item.value);
        }
        return { value: list };
    }
    if (is_plain_object(value)) {
        const keys = Object.keys(value);
        if (keys.length > limits.CUSTOM_ARRAY_MAX) {
            return { invalid: true };
        }
        const result = {};
        for (let i = 0; i < keys.length; i += 1) {
            if (keys[i] === "__proto__" || keys[i].length > LIMITS.EXTRA_META_KEY_MAX) {
                return { invalid: true };
            }
            const item = clean_json(value[keys[i]], limits, depth + 1);
            if (item.invalid) {
                return item;
            }
            result[keys[i]] = item.value;
        }
        return { value: result };
    }
    return { invalid: true };
};

const validate_custom = (raw, field, context) => {
    const known = context.custom_fields?.[field.type.slice(CUSTOM_FIELD_PREFIX.length)];
    const value_type = known?.value_type;
    const expected = {
        string: (value) => typeof value === "string",
        number: (value) => typeof value === "number",
        boolean: (value) => typeof value === "boolean",
        array: (value) => Array.isArray(value),
        object: (value) => is_plain_object(value),
    };
    if (value_type && expected[value_type] && !expected[value_type](raw)) {
        return fail("invalid_type", { expected: value_type });
    }
    const cleaned = clean_json(raw, context.limits, 0);
    return cleaned.invalid ? fail("invalid_custom_value") : ok(cleaned.value);
};

// Validates one raw value against one field (no required / visibility handling).
// context: { limits, mode, geo, custom_fields, current_state_value }
const validate_field_value = (field, raw, context = {}) => {
    const ctx = { ...context, limits: { ...LIMITS, ...(context.limits || {}) }, mode: context.mode === "stored" ? "stored" : "input" };
    const type = field?.type;
    if (OPTION_FIELD_TYPES.includes(type)) {
        return MULTI_OPTION_FIELD_TYPES.includes(type) ? validate_multi_option(raw, field) : validate_single_option(raw, field);
    }
    switch (type) {
        case "text":
            return validate_text(raw, field, ctx.limits, false, ctx.regex_test);
        case "textarea":
            return validate_text(raw, field, ctx.limits, true, ctx.regex_test);
        case "number":
            return validate_number(raw, field);
        case "price":
            return validate_price(raw);
        case "date":
            return validate_date(raw, field);
        case "file":
            return validate_file(raw, field, ctx.limits, ctx.mode);
        case "mobile":
            return validate_mobile(raw);
        case "email":
            return validate_email(raw);
        case "national_code":
            return validate_national_code(raw);
        case "postal_code":
            return validate_postal_code(raw);
        case "state":
            return validate_geo(raw, field, ctx, "state");
        case "city":
            return validate_geo(raw, field, ctx, "city");
        default:
            if (typeof type === "string" && type.startsWith(CUSTOM_FIELD_PREFIX)) {
                return validate_custom(raw, field, ctx);
            }
            return fail("invalid_type");
    }
};

const is_empty_clean = (value) => value === undefined || is_empty_value(value);

// The rules on a number or a price (`validation_conditions`, AND, an item may be an `or` group): do they hold for `own_value`, given the
// other values of the form (`read_other(key)` reads one, in the same group instance for a repeatable group)? A rule that reads a field the
// visitor left empty says nothing (it cannot be judged yet - the field that is required says so), so such a rule passes.
const validation_rules_hold = (field, own_value, read_other) => {
    const rules = Array.isArray(field.validation_conditions) ? field.validation_conditions : [];
    if (rules.length === 0 || !VALIDATION_FIELD_TYPES.includes(field.type)) {
        return true;
    }
    const read = (key) => (key === field.key ? own_value : read_other(key));
    return rules.every((item) => {
        const refs = condition_field_refs(item);
        if (refs.some((key) => is_empty_value(comparable(read(key))))) {
            return true;
        }
        return evaluate_condition_item(item, read);
    });
};

const validate_submission = (version, values, options = {}) => {
    const limits = { ...LIMITS, ...(options.limits || {}) };
    const context = {
        limits,
        mode: options.mode === "stored" ? "stored" : "input",
        geo: options.geo,
        custom_fields: options.custom_fields,
        regex_test: typeof options.regex_test === "function" ? options.regex_test : undefined,
        current_state_value: undefined,
    };
    const errors = [];
    const clean_values = {};

    if (!is_plain_object(values)) {
        errors.push({ key: null, group_key: null, index: null, path: "", code: "invalid_values" });
        return { clean_values, errors };
    }
    if (values_json_size(values) > limits.VALUES_JSON_MAX_BYTES) {
        errors.push({ key: null, group_key: null, index: null, path: "", code: "values_too_large", max: limits.VALUES_JSON_MAX_BYTES });
        return { clean_values, errors };
    }

    const state = resolve_form_state(version, values);
    const groups = as_list(version?.groups);

    const add_error = (key, group_key, index, code, details = {}) => {
        errors.push({ key, group_key, index, path: group_key ? `${group_key}.${index}.${key}` : key, code, ...details });
    };

    // One field: returns the clean value or undefined; pushes errors.
    const process_field = (field, field_state, raw, group_key, index, read_other = () => undefined) => {
        if (!field_state || !field_state.visible) {
            return undefined;
        }
        let source = raw;
        let from_default = false;
        if (field_state.disabled) {
            // A disabled field cannot be edited: only its default survives.
            if (field.default_value === undefined || is_empty_value(field.default_value)) {
                return undefined;
            }
            source = field.default_value;
            from_default = true;
        }
        let result = { value: undefined };
        if (!is_empty_value(source)) {
            result = validate_field_value(field, source, context);
            if (result.error) {
                if (!from_default) {
                    add_error(field.key, group_key, index, result.error.code, result.error);
                }
                return undefined;
            }
            if (field.type === "state") {
                context.current_state_value = result.value?.value;
            }
        }
        if (is_empty_clean(result.value)) {
            if (field_state.required && !field_state.disabled) {
                add_error(field.key, group_key, index, "required");
            }
            return undefined;
        }
        if (!from_default && !validation_rules_hold(field, result.value, read_other)) {
            add_error(field.key, group_key, index, "validation_failed");
            return undefined;
        }
        return result.value;
    };

    // Keys the version knows at the top level.
    const top_level_keys = new Set();
    groups.forEach((group) => {
        if (group?.is_repeatable === true) {
            top_level_keys.add(group.key);
        } else {
            as_list(group?.fields).forEach((field) => top_level_keys.add(field?.key));
        }
    });
    Object.keys(values).forEach((key) => {
        if (!top_level_keys.has(key)) {
            add_error(key, null, null, "unknown_key");
        }
    });

    groups.forEach((group) => {
        if (!group || !state.groups[group.key] || !state.groups[group.key].visible) {
            return;
        }
        if (group.is_repeatable !== true) {
            context.current_state_value = undefined;
            as_list(group.fields).forEach((field) => {
                if (!field || state.fields[field.key] === undefined) {
                    return;
                }
                const raw = has_own(values, field.key) ? values[field.key] : undefined;
                const value = process_field(field, state.fields[field.key], raw, null, null, (key) => (has_own(values, key) ? values[key] : undefined));
                if (value !== undefined) {
                    clean_values[field.key] = value;
                }
            });
            return;
        }

        const raw_list = has_own(values, group.key) ? values[group.key] : undefined;
        if (raw_list !== undefined && raw_list !== null && !Array.isArray(raw_list)) {
            add_error(group.key, null, null, "invalid_group_value");
            return;
        }
        if (Array.isArray(raw_list) && raw_list.length > limits.REPEAT_MAX) {
            add_error(group.key, null, null, "repeat_limit", { max: limits.REPEAT_MAX });
            return;
        }
        const instances = [];
        const count = Math.max(1, as_list(raw_list).length);
        const field_keys = new Set(as_list(group.fields).map((field) => field?.key));
        for (let index = 0; index < count; index += 1) {
            const raw_instance = as_list(raw_list)[index];
            if (raw_instance !== undefined && !is_plain_object(raw_instance)) {
                add_error(group.key, null, null, "invalid_group_value", { index });
                instances.push({});
                continue;
            }
            const instance_values = raw_instance || {};
            Object.keys(instance_values).forEach((key) => {
                if (!field_keys.has(key)) {
                    add_error(key, group.key, index, "unknown_key");
                }
            });
            const instance_state = state.instances[group.key]?.[index] || {};
            const instance = {};
            context.current_state_value = undefined;
            as_list(group.fields).forEach((field) => {
                if (!field || instance_state[field.key] === undefined) {
                    return;
                }
                const raw = has_own(instance_values, field.key) ? instance_values[field.key] : undefined;
                const value = process_field(field, instance_state[field.key], raw, group.key, index, (key) => (has_own(instance_values, key) ? instance_values[key] : undefined));
                if (value !== undefined) {
                    instance[field.key] = value;
                }
            });
            instances.push(instance);
        }
        if (instances.some((instance) => Object.keys(instance).length > 0)) {
            clean_values[group.key] = instances;
        }
    });

    return { clean_values, errors };
};

export {
    validate_submission,
    validate_field_value,
    clean_string,
    allowed_file_types,
};
